import {
  businessEntityKeys,
  queryClient,
  updateBusinessEntity,
  useJurisdictions,
} from '@commenda/api';
import { Switch, useToggle } from '@commenda/kakashi';
import mixpanel, {
  MIXPANEL_SOURCE,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import { IBusinessEntity } from '@commenda/types';
import {
  copyToClipboard,
  getPrintableCorporationType,
  MonthIndexOfMonth,
  NonTimezoneSensitiveDate,
} from '@commenda/utils';
import { Country as CountryPkg } from 'country-state-city';
import { RequestCallbackDialog } from '../../../../Contact/RequestCallbackModal';
import { useNotification } from '../../../../hooks/useNotification';
import { AddEntityModal } from '../../../common/BusinessEntity/AddEntityModal';
import { DataPoint } from '../../../common/DataPoint';
import { EditBusinessEntityCodeModal } from './Modals/EditBusinessEntityCodeModal';
import { EditBusinessEntityDescriptionModal } from './Modals/EditBusinessEntityDescriptionModal';
import { EditBusinessEntityWebsiteModal } from './Modals/EditBusinessEntityWebsiteModal';
import { EditDateOfClosureModal } from './Modals/EditDateOfClosureModal';

export const GeneralInformation = ({
  businessEntity,
  hideTitle = false,
  fieldsToHide = {
    businessCode: false,
    businessDescription: false,
    website: false,
  },
  hideEditIconForFields = {
    entityName: false,
    entityType: false,
    jurisdiction: false,
    dateOfIncorporation: false,
    fiscalYearEnd: false,
    businessDescription: false,
    businessCode: false,
    website: false,
  },
  hideCopyIconForFields = {
    entityName: false,
    entityType: false,
    jurisdiction: false,
    dateOfIncorporation: false,
    fiscalYearEnd: false,
    businessDescription: false,
    businessCode: false,
    website: false,
  },
  disabled = false,
  isCommendaAdmin = false,
}: {
  businessEntity: IBusinessEntity;
  hideTitle?: boolean;
  fieldsToHide?: {
    businessCode?: boolean;
    businessDescription?: boolean;
    website?: boolean;
  };
  hideEditIconForFields?: {
    entityName?: boolean;
    entityType?: boolean;
    jurisdiction?: boolean;
    dateOfIncorporation?: boolean;
    fiscalYearEnd?: boolean;
    businessDescription?: boolean;
    businessCode?: boolean;
    website?: boolean;
  };
  hideCopyIconForFields?: {
    entityName?: boolean;
    entityType?: boolean;
    jurisdiction?: boolean;
    dateOfIncorporation?: boolean;
    fiscalYearEnd?: boolean;
    businessDescription?: boolean;
    businessCode?: boolean;
    website?: boolean;
  };
  disabled?: boolean;
  isCommendaAdmin?: boolean;
}) => {
  const { setNotification } = useNotification();
  const { labelOf, optionsFor } = useJurisdictions();
  const requestCallbackDialogVisibility = useToggle();
  const editBusinessDescriptionModalVisibility = useToggle();
  const editBusinessCodeModalVisibility = useToggle();
  const editWebsiteModalVisibility = useToggle();
  const editDateOfClosureModalVisibility = useToggle();

  const addEntityModalVisibility = useToggle();

  // Worth its own row only where the country has more than one jurisdiction to be in — the same
  // test the add-entity form uses to decide whether to ask. Anywhere else it restates the country.
  const hasJurisdictionToShow =
    optionsFor(businessEntity.generalCorporationInfo?.incorporationCountry)
      .length > 1;

  const refreshBusinessEntities = () => {
    queryClient.invalidateQueries({
      queryKey: [businessEntityKeys.FETCH_BUSINESS_ENTITY, +businessEntity.id],
    });
    queryClient.invalidateQueries({
      queryKey: [
        businessEntityKeys.FETCH_BUSINESS_ENTITIES_OF_COMPANY,
        +businessEntity.generalCorporationInfo.companyId,
      ],
    });
  };

  const isIncorporationInProgress =
    !!businessEntity.featureSpecificInfo?.incorporation
      ?.isIncorporationInProgress;

  const handleIncorporationInProgressToggle = async (checked: boolean) => {
    const res = await updateBusinessEntity(businessEntity.id, {
      isIncorporationInProgress: checked,
    });

    if (!res.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to update incorporation status',
        message: 'Please try again later.',
      });
      return;
    }

    setNotification({
      show: true,
      valid: true,
      title: 'Incorporation status updated',
      message: `Incorporation in progress is now ${checked ? 'enabled' : 'disabled'}.`,
    });

    refreshBusinessEntities();
  };

  // Certain fields are necessary for compliance calendar generation and should not be editable by the user
  // once the compliance calendar is generated,
  // But that information needs to be populated in the first place by the user, so if it's not
  // available, we should allow the user to edit it.
  function isEditSensitiveFieldAllowed(
    isAdmin: boolean,
    entity: IBusinessEntity
  ) {
    if (isAdmin) return true;

    if (
      entity.generalCorporationInfo.legalName &&
      entity.generalCorporationInfo.formationDate &&
      entity.accountingInfo?.fiscalYear?.data
    )
      return false;

    return true;
  }

  return (
    <div className="flex flex-col gap-4xl">
      <RequestCallbackDialog
        isOpen={requestCallbackDialogVisibility.visible}
        onClose={requestCallbackDialogVisibility.off}
      />
      {addEntityModalVisibility.visible ? (
        <AddEntityModal
          isOpen={addEntityModalVisibility.visible}
          onClose={addEntityModalVisibility.hide}
          companyId={businessEntity.generalCorporationInfo.companyId}
          businessEntityId={businessEntity.id}
          shouldNotResetForm={false}
          mode={isCommendaAdmin ? 'admin' : 'user'}
          allowEmptyEntityName={isCommendaAdmin}
          onSuccess={async () => {
            refreshBusinessEntities();
            mixpanel.track('ENTITY_GENERAL_INFO_UPDATED', {
              source: MIXPANEL_SOURCE.ENTITY_MANAGEMENT,
              surface_area: MIXPANEL_SURFACE_AREA.ENTITY_MANAGEMENT,
              business_entity_id: businessEntity.id,
              company_id: businessEntity.generalCorporationInfo.companyId,
              field: 'entity_core_details',
            });
          }}
        />
      ) : null}
      <EditDateOfClosureModal
        isOpen={editDateOfClosureModalVisibility.visible}
        onClose={editDateOfClosureModalVisibility.off}
        businessEntity={businessEntity}
        onSuccess={() => {
          refreshBusinessEntities();

          mixpanel.track('ENTITY_GENERAL_INFO_UPDATED', {
            source: MIXPANEL_SOURCE.ENTITY_MANAGEMENT,
            surface_area: MIXPANEL_SURFACE_AREA.ENTITY_MANAGEMENT,
            business_entity_id: businessEntity.id,
            company_id: businessEntity.generalCorporationInfo.companyId,
            field: 'date_of_closure',
          });
        }}
      />
      <EditBusinessEntityDescriptionModal
        isOpen={editBusinessDescriptionModalVisibility.visible}
        onClose={editBusinessDescriptionModalVisibility.off}
        businessEntity={businessEntity}
        onSuccess={async () => {
          refreshBusinessEntities();

          mixpanel.track('ENTITY_GENERAL_INFO_UPDATED', {
            source: MIXPANEL_SOURCE.ENTITY_MANAGEMENT,
            surface_area: MIXPANEL_SURFACE_AREA.ENTITY_MANAGEMENT,
            business_entity_id: businessEntity.id,
            company_id: businessEntity.generalCorporationInfo.companyId,
            field: 'business_description',
          });

          setNotification({
            show: true,
            valid: true,
            title: 'Success',
            message: 'Business description updated',
          });
        }}
      />
      <EditBusinessEntityCodeModal
        isOpen={editBusinessCodeModalVisibility.visible}
        onClose={editBusinessCodeModalVisibility.off}
        businessEntity={businessEntity}
        onSuccess={() => {
          refreshBusinessEntities();

          mixpanel.track('ENTITY_GENERAL_INFO_UPDATED', {
            source: MIXPANEL_SOURCE.ENTITY_MANAGEMENT,
            surface_area: MIXPANEL_SURFACE_AREA.ENTITY_MANAGEMENT,
            business_entity_id: businessEntity.id,
            company_id: businessEntity.generalCorporationInfo.companyId,
            field: 'business_code',
          });

          setNotification({
            show: true,
            valid: true,
            title: 'Success',
            message: 'Business code updated.',
          });
        }}
        isEditable
      />
      <EditBusinessEntityWebsiteModal
        isOpen={editWebsiteModalVisibility.visible}
        onClose={editWebsiteModalVisibility.off}
        businessEntity={businessEntity}
        onSuccess={() => {
          refreshBusinessEntities();

          mixpanel.track('ENTITY_GENERAL_INFO_UPDATED', {
            source: MIXPANEL_SOURCE.ENTITY_MANAGEMENT,
            surface_area: MIXPANEL_SURFACE_AREA.ENTITY_MANAGEMENT,
            business_entity_id: businessEntity.id,
            company_id: businessEntity.generalCorporationInfo.companyId,
            field: 'website',
          });

          setNotification({
            show: true,
            valid: true,
            title: 'Success',
            message: 'Website updated.',
          });
        }}
      />

      <div className="flex flex-col gap-2xl">
        {!hideTitle && (
          <div className="flex flex-col gap-xs">
            <h3 className="txt-large-bold">General Business Information</h3>
            <p className="txt-small-regular text-text-tertiary">
              Essential business details about your entity.
            </p>
          </div>
        )}
        <div className="rounded-xl overflow-clip divide-y divide-border-primary">
          <DataPoint
            title="Entity name"
            value={businessEntity.generalCorporationInfo.legalName}
            disabled={disabled}
            onCopy={
              !hideCopyIconForFields.entityName
                ? () => {
                  copyToClipboard(
                    businessEntity.generalCorporationInfo.legalName
                  );
                  setNotification({
                    show: true,
                    valid: true,
                    title: 'Copied to clipboard',
                    message: 'Entity name copied to clipboard',
                  });
                }
                : undefined
            }
            editConfig={
              !hideEditIconForFields.entityName
                ? {
                  isAllowed: true,
                  onEdit: isEditSensitiveFieldAllowed(
                    isCommendaAdmin,
                    businessEntity
                  )
                    ? addEntityModalVisibility.show
                    : requestCallbackDialogVisibility.show,
                }
                : undefined
            }
          />
          <DataPoint
            title="Incorporation country"
            value={
              businessEntity.generalCorporationInfo.incorporationCountry
                ? CountryPkg.getCountryByCode(
                  businessEntity.generalCorporationInfo.incorporationCountry
                )?.name
                : null
            }
            disabled={disabled}
          />
          <DataPoint
            title="Entity type"
            value={getPrintableCorporationType(
              businessEntity.generalCorporationInfo.businessEntityType,
              businessEntity.generalCorporationInfo.incorporationCountry
            )}
            disabled={disabled}
            onCopy={
              !hideCopyIconForFields.entityType
                ? () => {
                  copyToClipboard(
                    businessEntity.generalCorporationInfo.businessEntityType
                  );
                  setNotification({
                    show: true,
                    valid: true,
                    title: 'Copied to clipboard',
                    message: 'Entity type copied to clipboard',
                  });
                }
                : undefined
            }
            editConfig={
              !hideEditIconForFields.entityType
                ? {
                  isAllowed: true,
                  onEdit: isEditSensitiveFieldAllowed(
                    isCommendaAdmin,
                    businessEntity
                  )
                    ? addEntityModalVisibility.show
                    : requestCallbackDialogVisibility.show,
                }
                : undefined
            }
          />
          {hasJurisdictionToShow ? (
            <DataPoint
              title="Jurisdiction"
              value={labelOf(
                businessEntity.generalCorporationInfo.jurisdiction
              )}
              disabled={disabled}
              onCopy={
                !hideCopyIconForFields.jurisdiction
                  ? () => {
                    copyToClipboard(
                      labelOf(
                        businessEntity.generalCorporationInfo.jurisdiction
                      )
                    );
                    setNotification({
                      show: true,
                      valid: true,
                      title: 'Copied to clipboard',
                      message: 'Jurisdiction copied to clipboard',
                    });
                  }
                  : undefined
              }
              editConfig={
                !hideEditIconForFields.jurisdiction
                  ? {
                    isAllowed: true,
                    onEdit: isEditSensitiveFieldAllowed(
                      isCommendaAdmin,
                      businessEntity
                    )
                      ? addEntityModalVisibility.show
                      : requestCallbackDialogVisibility.show,
                  }
                  : undefined
              }
            />
          ) : null}
          <DataPoint
            title="Date of incorporation"
            value={
              businessEntity.generalCorporationInfo?.formationDate
                ? NonTimezoneSensitiveDate.fromISO8601String(
                  businessEntity.generalCorporationInfo.formationDate
                ).format('MMMM D, YYYY')
                : null
            }
            disabled={disabled}
            onCopy={
              !hideCopyIconForFields.dateOfIncorporation
                ? () => {
                  copyToClipboard(
                    businessEntity.generalCorporationInfo.formationDate
                  );
                  setNotification({
                    show: true,
                    valid: true,
                    title: 'Copied to clipboard',
                    message: 'Date of incorporation copied to clipboard',
                  });
                }
                : undefined
            }
            editConfig={
              !hideEditIconForFields.dateOfIncorporation
                ? {
                  isAllowed: true,
                  onEdit: isEditSensitiveFieldAllowed(
                    isCommendaAdmin,
                    businessEntity
                  )
                    ? addEntityModalVisibility.show
                    : requestCallbackDialogVisibility.show,
                }
                : undefined
            }
          />
          <DataPoint
            title="Fiscal year end"
            disabled={disabled}
            value={
              businessEntity.accountingInfo?.fiscalYear?.data?.FIXED_DATE
                ?.day &&
                businessEntity.accountingInfo?.fiscalYear?.data?.FIXED_DATE?.month
                ? NonTimezoneSensitiveDate.fromNativeDate(
                  new Date(
                    new Date().getFullYear(),
                    MonthIndexOfMonth[
                    businessEntity.accountingInfo.fiscalYear.data.FIXED_DATE
                      .month
                    ] - 1,
                    +businessEntity.accountingInfo.fiscalYear.data.FIXED_DATE
                      .day
                  )
                ).format('MMMM D')
                : null
            }
            onCopy={() => {
              if (
                businessEntity.accountingInfo?.fiscalYear?.data?.FIXED_DATE &&
                businessEntity.accountingInfo?.fiscalYear?.data?.FIXED_DATE
                  ?.day &&
                businessEntity.accountingInfo?.fiscalYear?.data?.FIXED_DATE
                  ?.month
              ) {
                copyToClipboard(
                  businessEntity.accountingInfo?.fiscalYear?.data?.FIXED_DATE?.day.toString() +
                  '/' +
                  MonthIndexOfMonth[
                  businessEntity.accountingInfo.fiscalYear.data.FIXED_DATE
                    .month
                  ] +
                  '/' +
                  new Date().getFullYear().toString()
                );
                setNotification({
                  show: true,
                  valid: true,
                  title: 'Copied to clipboard',
                  message: 'Fiscal year end copied to clipboard',
                });
              }
              setNotification({
                show: true,
                valid: true,
                title: 'Copied to clipboard',
                message: 'Fiscal year end copied to clipboard',
              });
            }}
            editConfig={
              !hideEditIconForFields.fiscalYearEnd
                ? {
                  isAllowed: true,
                  onEdit: isEditSensitiveFieldAllowed(
                    isCommendaAdmin,
                    businessEntity
                  )
                    ? addEntityModalVisibility.show
                    : requestCallbackDialogVisibility.show,
                }
                : undefined
            }
          />
          {!fieldsToHide.website && (
            <DataPoint
              title="Website"
              value={
                businessEntity.generalCorporationInfo.publicFacingWebsiteUrl
              }
              disabled={disabled}
              onCopy={
                !hideCopyIconForFields.website
                  ? () => {
                    const url =
                      businessEntity.generalCorporationInfo
                        .publicFacingWebsiteUrl;
                    if (url) {
                      copyToClipboard(url);
                      setNotification({
                        show: true,
                        valid: true,
                        title: 'Copied to clipboard',
                        message: 'Website copied to clipboard',
                      });
                    }
                  }
                  : undefined
              }
              editConfig={
                !hideEditIconForFields.website
                  ? {
                    isAllowed: true,
                    onEdit: editWebsiteModalVisibility.show,
                  }
                  : undefined
              }
            />
          )}
          {!fieldsToHide.businessDescription && (
            <DataPoint
              title="Business description"
              value={businessEntity.generalCorporationInfo.businessDescription}
              disabled={disabled}
              onCopy={
                !hideCopyIconForFields.businessDescription
                  ? () => {
                    copyToClipboard(
                      businessEntity.generalCorporationInfo
                        .businessDescription
                    );
                    setNotification({
                      show: true,
                      valid: true,
                      title: 'Copied to clipboard',
                      message: 'Business description copied to clipboard',
                    });
                  }
                  : undefined
              }
              editConfig={
                !hideEditIconForFields.businessDescription
                  ? {
                    isAllowed: true,
                    onEdit: editBusinessDescriptionModalVisibility.show,
                  }
                  : undefined
              }
            />
          )}
          {!fieldsToHide.businessCode && (
            <DataPoint
              title="Business code"
              value={businessEntity.generalCorporationInfo.industryCode?.value}
              onCopy={
                !hideCopyIconForFields.businessCode
                  ? () => {
                    const value =
                      businessEntity.generalCorporationInfo.industryCode
                        ?.value;
                    if (value) {
                      copyToClipboard(value);
                      setNotification({
                        show: true,
                        valid: true,
                        title: 'Copied to clipboard',
                        message: 'Business code copied to clipboard',
                      });
                    }
                  }
                  : undefined
              }
              editConfig={
                !hideEditIconForFields.businessCode
                  ? {
                    isAllowed: true,
                    onEdit: editBusinessCodeModalVisibility.show,
                  }
                  : undefined
              }
              disabled={disabled}
            />
          )}
          {businessEntity.generalCorporationInfo?.dateOfClosure && (
            <DataPoint
              title="Date of closure"
              value={businessEntity.generalCorporationInfo.dateOfClosure}
              onCopy={() => {
                setNotification({
                  show: true,
                  valid: true,
                  title: 'Copied to clipboard',
                  message: 'Address of registered agent copied to clipboard',
                });
              }}
              editConfig={{
                isAllowed: true,
                onEdit: isCommendaAdmin
                  ? editDateOfClosureModalVisibility.on
                  : requestCallbackDialogVisibility.show,
              }}
            />
          )}
          {isCommendaAdmin && (
            <DataPoint
              title="Incorporation in progress"
              value={isIncorporationInProgress ? 'Yes' : 'No'}
              reactNode={
                <Switch
                  checked={isIncorporationInProgress}
                  onCheckedChange={handleIncorporationInProgressToggle}
                />
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};
